// packages/contracts/src/refusal-remedy.ts
import { REFUSAL_KINDS, type RefusalKind } from './refusal.js'

/**
 * ★ What a person on the other side is supposed to DO about a refusal.
 *
 * ## Why this exists and why it is not on the row
 *
 * Annex A stories 9 and 10 ask, in so many words, for *"guidance on required
 * corrections or resubmission steps"*. That is not the same thing as the reason:
 * the reason says which door is shut, the remedy says where the key is. A
 * refusal that ships without its way out just moves the trap one door along —
 * the argument `BILL_OUTSIDE_GRACE_PERIOD` already carries in `refusal.ts`.
 *
 * ⚠ **Derived at render time from the kind, never stored on the row.** Two
 * consequences, both deliberate:
 *
 *  - A wording improvement is a code change, not a migration. Storing the text
 *    would put a copy of it on every historical row, so improving it would mean
 *    a data-modifying migration against tables that are append-only.
 *  - An old row cannot keep stale guidance. A record refused in March and read
 *    in September gets today's remedy, which is the correct answer to "what do I
 *    do now" — and the only one that can ever be right.
 *
 * ## The two fields, and why the split IS the control
 *
 * `reason_kind` — this closed union — is **safe to show the counterparty**, and
 * so is the remedy derived from it. `reason_detail` is free text for the audit
 * trail alone: it never enters an outbound file and never a counterparty-facing
 * response. That mirrors `audit-hook.ts` exactly, where `outcome` is what the
 * server decided and `detail.status` is what the client was told, and the two
 * are deliberately allowed to diverge.
 *
 * So every string below is written to be read by a Healthcare Service Provider
 * or a Payment Agency. None of them may disclose whether a record, a bill or an
 * officer exists, or whether it belongs to someone else — see
 * `ADJUSTMENT_TARGET_UNAVAILABLE`, whose single remedy has to serve five
 * different internal causes without distinguishing them.
 */
export const REFUSAL_REMEDY: Readonly<Record<RefusalKind, string>> = {
  // ── The refusals the pro-ration domain already raised ──────────────────────
  NO_ENTITLEMENT:
    'Confirm the date of medical service on this record. If it is correct, ask the officer’s ' +
    'entitlement agency to supply a record covering that date. PRS does not create entitlement records.',
  ENTITLEMENT_NOT_COVERING:
    'Correct the date of medical service if it was mis-keyed. Otherwise ask the entitlement agency to ' +
    'extend or supersede the entitlement period so that it covers that date, then resend the record.',
  AMBIGUOUS_ENTITLEMENT:
    'Ask the officer’s entitlement agency to correct its feed: two entitlement periods cover this ' +
    'date of medical service and PRS will not choose between them. Resend once one has been closed.',
  NO_RULE_IN_FORCE:
    'Confirm the date of medical service. If it is correct, the pro-ration rule for this benefit scheme ' +
    'has no version covering that date and PSD must author one before the bill can be computed.',
  SCHEME_NOT_ENABLED:
    'Contact PSD: this benefit scheme is not confirmed for use in PRS, and no bill on it can be ' +
    'pro-rated until it is.',
  // ★★★ WAVE 12 (P5). Addressed to PSD, not to the sender: the record is
  // correct and the two sides of PRS disagree about it. Both meanings are named
  // in the refusal detail, so the reader does not have to go and find them.
  ADJUSTMENT_INDICATOR_UNRECONCILED:
    'Ask PSD to reconcile the adjustment-indicator register with the published file-exchange grammar: ' +
    'supersede the register entry, or raise a change to the grammar, never both. The refusal names both ' +
    'meanings, and which one is right decides how this money is classified.',
  NO_REFERENCE_VALUE_IN_FORCE:
    'Ask PSD to author a pro-ration configuration entry covering this date of medical service. PRS will ' +
    'not substitute a default, because an unfunded default is money PSD never approved.',
  BILL_OUTSIDE_GRACE_PERIOD:
    'Check the date of medical service against the grace period for this bill type. If the delay is ' +
    'justified, ask PSD to supersede the grace period with a window covering that date; otherwise ' +
    'withdraw the bill.',
  WARD_BELOW_SERVICE_INJURY_MINIMUM:
    'Check the ward class this bill was submitted under against the minimum this service-injury ' +
    'case requires, named in the refusal. Correct the ward class if it was mis-keyed, or ask PSD to ' +
    'supersede the minimum if it no longer reflects policy.',
  PEGGED_RATE_EXCESS_BEARER_UNSUPPORTED:
    'Ask PSD to author a PEGGED_RATE_EXCESS_BEARER policy covering this date of medical service if ' +
    'none is in force, or to choose GOVERNMENT or OFFICER if PROVIDER is set: this pro-ration split ' +
    'cannot reduce what is paid to a Healthcare Service Provider below the billed amount.',
  MBIM_RECORD_EXPIRED:
    'Ask PSD or the MBIM office to renew the officer’s MBIM record (a new record with a later ' +
    'issued date) before this bill can be computed, or to review MBIM_MAX_VALIDITY if the ' +
    'validity period no longer reflects policy.',

  // ── The file-exchange path ────────────────────────────────────────────────
  RECORD_MALFORMED:
    'Correct this record against the published layout and resend it in a later file. A value containing ' +
    'the field delimiter or a line break, a date that is not YYYY-MM-DD, and a non-numeric amount are ' +
    'the usual causes.',
  UNKNOWN_OFFICER:
    'Check the officer identifier on this record against the one the entitlement agency issued, correct ' +
    'it, and resend the record in a later file.',
  DUPLICATE_SOURCE_REFERENCE:
    'Check your own records: a bill carrying this reference has already been received and its outcome ' +
    'already reported. Resend only under a new reference, and only if this is genuinely a different bill.',
  // ⚠ ONE remedy for FIVE internal causes (see the kind's own docstring): the
  // original does not exist, is another counterparty's, was rejected, is
  // itself an adjustment, or was compensated (C4, final review — REVERSED).
  // It must stay true of all five and must not let a reader tell which one
  // applied — they are indistinguishable to the sender by design, and that is
  // the whole point of the kind.
  //
  // ⚠ It still mentions "has been computed", and that is deliberate even though
  // an uncomputed original now has its OWN kind
  // (ORIGINAL_RESOLUTION_UNREPRODUCIBLE, with its own remedy naming the compute
  // route). Listing one more condition than the kind can actually mean is
  // harmless here and is the safer direction: a remedy that enumerated exactly
  // the reachable causes would narrow what the sender can infer, which is the
  // opposite of what this text is for.
  ADJUSTMENT_TARGET_UNAVAILABLE:
    'Confirm the original bill reference on this adjustment, and that the original was submitted by you, ' +
    'was accepted, is not itself an adjustment, and has been computed. Resend the adjustment once the ' +
    'original is in that state.',
  ADJUSTMENT_SERVICE_DATE_MISMATCH:
    'Correct the date of medical service on this adjustment so that it matches the original bill’s. ' +
    'A correction for services delivered on a different date is a new bill, not an adjustment.',
  ADJUSTMENT_BELOW_ZERO:
    'Check the adjustment amount: applying it would take the bill below zero. Resend with an amount no ' +
    'larger than the bill’s current total, or withdraw the bill instead.',
  ADJUSTMENT_ORIGINAL_CAPPED:
    'Contact PSD with this bill reference: it carries a subsidy cap that a flat-rate adjustment cannot ' +
    'reproduce, and the cap must be re-applied to the corrected total by hand. Do not resend under a ' +
    'new reference: the original still stands and nothing on it has changed.',
  ORIGINAL_RESOLUTION_UNREPRODUCIBLE:
    'Ask for the original bill to be computed first. An adjustment reproduces the rule and rate the ' +
    'original was computed under, and this original has none recorded. Resend the adjustment afterwards.',
  COUNTERPARTY_MISMATCH:
    'Correct the counterparty code in the file header to the one this channel is registered to, and ' +
    'resend the whole file. PRS takes the sender from the channel it arrived on, never from the file.',
  CONTROL_TOTAL_MISMATCH:
    'Recalculate the trailer’s record count and signed amount total, and resend the whole file. No ' +
    'record in it was processed: a total that disagrees means the file was not read as it was written.',
  LAYOUT_VERSION_UNSUPPORTED:
    'Check the layout identifier and version in the file header against the published layouts, and ' +
    'resend the whole file under a supported version.',
  // ⚠ It must say what to do about a record that is CORRECT and still refused.
  // Sending the reader to re-check the layout — RECORD_MALFORMED's remedy — would
  // cost them a cycle to discover the layout was never the problem.
  RECORD_TYPE_NOT_ACCEPTED:
    'Check which record types this exchange channel is registered to accept: new bills, upward ' +
    'adjustments and downward adjustments are authorised separately. Ask PSD to extend the ' +
    'registration if this channel should carry this one; the rest of the file was processed.',

  // ── Payment status (Annex A story 7) ──────────────────────────────────────
  //
  // ⚠ ONE remedy for FIVE internal causes, exactly as ADJUSTMENT_TARGET_UNAVAILABLE
  // has one for four. It must stay true of all five — absent, not yours, never
  // emitted, rejected, not covered by a grant — and must not let a reader tell
  // which applied.
  PAYMENT_TARGET_UNAVAILABLE:
    'Confirm the bill reference against the settlement instruction PRS sent you. A payment can only ' +
    'be applied to a bill that was released to you and is still open; report a payment you cannot ' +
    'place to PSD rather than resending it against a different reference.',
  // ⚠ It may name the balance: this is reachable only for a bill the sender can
  // already read in full. It must ALSO say what happens when the bill was right
  // and the amount was not, because the sender's next question is "so where did
  // my money go" — and the answer is that nothing was recorded.
  PAYMENT_EXCEEDS_BILL:
    'Check the amount against the balance outstanding on this bill: PRS records no payment that ' +
    'would take the total paid above what the bill is worth, and nothing on this record was applied. ' +
    'Resend for no more than the balance, and report a genuine overpayment to PSD.',
  PAYMENT_STATUS_INCONSISTENT:
    'Correct the payment status on this record: it states the bill’s position AFTER this instalment, ' +
    'so an instalment that settles the balance is PAID and one that leaves part outstanding is ' +
    'PARTIALLY_PAID. Pending and rejected records must carry a zero amount.',
}

/**
 * The longest a remedy may be.
 *
 * ⚠ **This is a bound on OUR OWN text**, checked by a test rather than at a
 * boundary, and that is the honest description of it. The remedy is derived
 * from the kind and never parsed from input, so there is nothing to validate at
 * runtime — but it IS rendered into a fixed-width field of an outbound file, so
 * an editor lengthening one is the way a counterparty's parser breaks. The test
 * is where that gets caught.
 *
 * P2C11 §14.6(c) makes a maximum mandatory for input; P2C3 §7.1.2 delegates the
 * value to a Whole-of-Government data standard whose parent §7.1.1 keeps it
 * behind an on-site viewing undertaking. So the number is OURS, the same way
 * `010_reject_reason_length.sql`'s 1,000 is (A-11 / Q-18); the whole published interface's bounds are A-22. `TODO(WOG-DATA-STD)`.
 */
export const MAX_REFUSAL_REMEDY_LENGTH = 300

/**
 * The longest `reason_detail` may be — the AUDIT-ONLY half.
 *
 * Same number as `MAX_REJECT_REASON_LENGTH` and for the same reason: this
 * field's job is to let an analyst reconstruct which of several
 * indistinguishable causes actually fired, and a paragraph does that. Unlike the
 * remedy, this one IS enforced at a boundary — in the pipeline that writes it
 * and by a `CHECK` at the database — because it is assembled from values that
 * arrived in a file.
 *
 * ⚠ Enforced at the database and not only in the writer, for the reason
 * `010_reject_reason_length.sql` states at length: a bound enforced only by the
 * caller is not a bound, and the database is the copy that holds when a batch
 * job, a migration or a psql session bypasses the other two.
 */
export const MAX_REFUSAL_DETAIL_LENGTH = 1000

/**
 * Verbs a remedy is allowed to open with.
 *
 * ⚠ Exported so the test asserts against the SAME list this file publishes,
 * rather than a second copy that can drift from it. The point of the list is
 * not the words — it is that a remedy has to begin with an instruction. A
 * sentence that restates the problem ("The original bill could not be found")
 * is a reason wearing a remedy's label, and it is what someone writes when a
 * new kind is added in a hurry.
 */
export const REMEDY_IMPERATIVES: readonly string[] = [
  'Ask',
  'Check',
  'Confirm',
  'Contact',
  'Correct',
  'Recalculate',
  'Resend',
  'Resubmit',
  'Submit',
  'Withdraw',
]

/** Does this text open with an instruction rather than a restatement? */
export function namesAnAction(remedy: string): boolean {
  return REMEDY_IMPERATIVES.some((verb) => remedy.startsWith(`${verb} `))
}

/** Every kind, so a consumer can assert totality at runtime as well as at compile time. */
export const REMEDY_KINDS: readonly RefusalKind[] = REFUSAL_KINDS
